import { nanoid } from 'nanoid/non-secure';
import { isNotNil, pickBy } from 'rambda';
import { RxQueue } from 'rx-queue';
import {
  asapScheduler,
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  fromEvent,
  interval,
  map,
  merge,
  type Observable,
  retry,
  Subject,
  Subscription,
  share,
  skipUntil,
  startWith,
  subscribeOn,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { type WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { refreshToken, shouldRefreshToken } from '@/hooks/auth/use-refresh-token';
import type I18n from '@/i18n';
import type { AuthType } from '@/models/auth';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { type DispatchEvent, dispatchEventScheme } from '@/models/events/dispatch-event';
import {
  ReceiveGroupMessageCommand,
  ReceiveMessageCommand,
  type ReceiveMessageType,
} from '@/models/messages/receive-message';
import { type SendChatMessageType, SendMessageCommand, type SendMessageType } from '@/models/messages/send-message';
import type { DynamicEndpointManager } from '@/services/dynamic-endpoint-manager';
import { useAuthStore } from '@/stores/auth';
import { useConfigurationStore } from '@/stores/configuration';
import { BRAND_STORAGE_KEY } from '@/utils/constants';
import { Device } from '@/utils/device-type';
import { getDeviceIdentifier } from '@/utils/get-standalone-mode';
import { transformLanguageCode } from '@/utils/language-code';
import { createLogger } from '@/utils/logger';

export function createWebSocketManager(dynamicEndpointManager: DynamicEndpointManager, i18n: typeof I18n) {
  return new WebSocketManager(dynamicEndpointManager, transformLanguageCode(i18n.resolvedLanguage ?? 'zh-CN'));
}

export type WebSocketState = 'open' | 'closed' | 'ended' | 'connecting' | 'connected';

export type WebSocketMessage = (SendMessageType | SendChatMessageType | ReceiveMessageType) & { D?: Device };

export default class WebSocketManager {
  readonly #dynamicEndpointManager: DynamicEndpointManager;
  readonly #logger = createLogger('Web Socket');
  #id: string | null = null;
  #socketId: string | null = null;
  #locale: string;

  #signedIn$ = new BehaviorSubject(false);
  #queue$ = new RxQueue<WebSocketMessage>();
  #heartbeat$ = new BehaviorSubject(0);
  #event$ = new Subject<DispatchEvent>();
  #state$ = new BehaviorSubject<WebSocketState>('closed');
  #checkConnection$ = new BehaviorSubject<number | null>(null);

  #socket$: WebSocketSubject<WebSocketMessage> | null = null;
  #subscriptions = new Subscription();

  constructor(dynamicEndpointManager: DynamicEndpointManager, locale: string) {
    this.#dynamicEndpointManager = dynamicEndpointManager;
    this.#locale = locale;
  }

  reconnect() {
    this.dispatch({
      action: 'reconnect',
      params: {},
    });
  }

  dispatch(event: DispatchEvent) {
    const parseResult = dispatchEventScheme.safeParse(event);

    if (!parseResult.success) {
      this.#logger.error('Dispatcher parse failed', parseResult.error.format());
      return;
    }

    const { action, params } = parseResult.data;

    this.#logger.info('Message dispatched', action, params);

    switch (action) {
      case 'connect':
        if (!this.#socket$ || this.#socket$.closed) {
          this.#createWebSocketConnection();
        }
        break;
      case 'disconnect':
        if (this.#socket$ && !this.#socket$.closed) {
          this.#heartbeat$.next(0);
          this.#socket$.complete();
          this.#socket$ = null;
        } else {
          this.#logger.warn('No socket established.');
        }
        break;
      case 'reconnect':
        this.#logger.info('Reconnecting');
        if (this.#socket$ && this.state !== 'connecting') {
          this.#heartbeat$.next(0);
          this.#createWebSocketConnection();
        } else {
          this.#logger.warn('No socket established.');
        }
        break;
      case 'localeChange':
        this.#locale = params.locale;
        if (this.state !== 'connecting') {
          this.#heartbeat$.next(0);
          this.#createWebSocketConnection();
        }
        break;
      case 'send':
        if (params.isResend) {
          this.#heartbeat$.next(0);
          this.#checkConnection$.next(Date.now());
          if (this.#socket$ && this.#auth) {
            this.#socket$.next({
              Cmd: SendMessageCommand.HeartBeat,
              UID: this.#auth.UserID,
              D: getDeviceIdentifier(),
            });
          }
        }
        this.#queue$.next(params.message);
        break;
      case 'checkConnection':
        this.#heartbeat$.next(0);
        this.#checkConnection$.next(Date.now());
        if (this.#socket$ && this.#auth) {
          this.#socket$.next({
            Cmd: SendMessageCommand.HeartBeat,
            UID: this.#auth.UserID,
            D: getDeviceIdentifier(),
          });
        }
        break;
      case 'unsubscribe':
        this.#auth &&
          this.#queue$.next({
            Cmd: SendMessageCommand.GroupUnsubscribe,
            UID: this.#auth.UserID,
            GroupID: params.groupId,
          });
        break;
      case 'deleteMessage':
        this.#auth &&
          this.#queue$.next({
            Cmd: params.type === 'session' ? SendMessageCommand.DeleteChat : SendMessageCommand.DeleteGroupChat,
            ChatID: params.messageId,
            GroupChatID: params.messageId,
            UserID: this.#auth.UserID,
            ChatSessionID: params.sessionId,
            GroupID: params.sessionId,
          });
        break;
      default:
        this.#logger.warn('Dispatch', 'invalid action', action, params);
        break;
    }
  }

  ensureConnected(): Promise<void> {
    if (this.state === 'connected') return Promise.resolve();

    return firstValueFrom(
      this.state$.pipe(
        filter((state) => state === 'connected'),
        map(() => undefined),
      ),
    );
  }

  get id(): string {
    if (this.#id === null) {
      this.#id = nanoid(12);
    }
    return this.#id;
  }

  get socketId(): string | null {
    return this.#socketId;
  }

  get state(): WebSocketState {
    return this.#state$.getValue();
  }

  get state$(): Observable<WebSocketState> {
    return this.#state$.asObservable().pipe(distinctUntilChanged(), share());
  }

  get event$(): Observable<DispatchEvent> {
    return this.#event$.asObservable().pipe(filter(isNotNil), subscribeOn(asapScheduler), share());
  }

  get #auth(): AuthType | null {
    return useAuthStore.getState().auth;
  }

  get #endpoint(): string {
    return useConfigurationStore.getState().endpoint.WEBSOCKET_ENDPOINT;
  }

  get #brand(): string | null {
    return localStorage.getItem(BRAND_STORAGE_KEY);
  }

  async #handleIncomingEvent(event: WebSocketMessage) {
    switch (event.Cmd) {
      case ReceiveMessageCommand.LoginResponse:
        if (event.Code === 0) {
          this.#logger.info('Signed in', { UserID: this.#auth?.UserID });
          this.#heartbeat$.next(Date.now());
          this.#state$.next('connected');
          this.#signedIn$.next(true);
        } else {
          if (shouldRefreshToken()) {
            const newToken = await refreshToken();
            if (newToken) {
              this.#createWebSocketConnection();
              break;
            }
          }
          this.#signedIn$.next(false);
          this.#event$.next({
            action: 'disconnect',
            params: {},
          });
        }
        break;
      case ReceiveMessageCommand.LogoutResponse:
        this.#signedIn$.next(false);
        break;
      case ReceiveMessageCommand.NewChatSession:
        this.#event$.next({
          action: 'newSession',
          params: {
            userId: event.FriendID,
            sessionId: event.ChatSessionID,
          },
        });
        break;
      case ReceiveMessageCommand.ChatMessage:
        this.#event$.next({
          action: 'message',
          params: {
            type: 'chat',
            id: event.chat.ChatSessionID,
            message: event.chat,
          },
        });
        break;
      case ReceiveMessageCommand.KickOut:
        if (shouldRefreshToken()) {
          const newToken = await refreshToken();
          if (newToken) {
            this.#createWebSocketConnection();
            break;
          }
        }
        this.#signedIn$.next(false);
        this.#event$.next({
          action: 'kickOut',
          params: {},
        });
        break;
      case ReceiveMessageCommand.FriendApplication:
        this.#event$.next({
          action: 'friendApplication',
          params: {
            applicationId: event.ApplicationID,
            userId: event.UserID,
            userName: event.NickName,
            avatarUrl: event.HeadImg,
            remark: event.Remark ?? '',
          },
        });
        break;
      case ReceiveMessageCommand.GroupChatMessage:
        this.#event$.next({
          action: 'message',
          params: {
            type: 'groupChat',
            id: event.groupchat.GroupID,
            message: event.groupchat,
          },
        });
        if (
          [ConversationMessageContentType.Notice, ConversationMessageContentType.AllBan].includes(
            event.groupchat.MsgType,
          )
        ) {
          if (event.GroupInfo !== null) {
            this.#event$.next({
              action: 'updateGroup',
              params: {
                groupId: event.GroupID,
                info: event.GroupInfo,
              },
            });
          }
          this.#event$.next({
            action: 'validateGroup',
            params: { groupId: event.GroupID },
          });
        }
        break;
      case ReceiveMessageCommand.ChatResponse:
        if (event.Code === 3) {
          this.#event$.next({
            action: 'toastInfo',
            params: { type: 'info', msg: event.Msg },
          });
        } else if (event.Code === 0) {
          this.#event$.next({
            action: 'validateMessage',
            params: {
              type: 'chat',
              validateId: event.TimeStamp,
              sessionId: event.ChatSessionID,
              messageId: event.ChatID,
            },
          });
        }
        break;
      case ReceiveMessageCommand.GroupChatResponse:
        if ([3, 5].includes(event.Code)) {
          this.#event$.next({
            action: 'validateGroup',
            params: { groupId: event.GroupID },
          });
        } else if (event.Code === 0) {
          this.#event$.next({
            action: 'validateMessage',
            params: {
              type: 'groupChat',
              validateId: event.TimeStamp,
              sessionId: event.GroupID,
              messageId: event.GroupChatID,
            },
          });
        }
        break;
      case ReceiveMessageCommand.ReadChat:
        if (event.ChatID === 0) {
          this.#event$.next({
            action: 'readAll',
            params: { sessionId: event.ChatSessionID },
          });
        } else {
          this.#event$.next({
            action: 'read',
            params: {
              sessionId: event.ChatSessionID,
              messageId: event.ChatID,
            },
          });
        }
        break;
      case ReceiveMessageCommand.ReadGroupChat:
        this.#event$.next({
          action: 'groupReadAll',
          params: { groupId: event.GroupID, messageId: event.MaxReadID },
        });
        break;
      case ReceiveMessageCommand.ReadChatSelf:
        this.#event$.next({
          action: 'readChatSelf',
          params: {
            sessionId: event.ChatSessionID,
            messageId: event.ChatID,
            timestamp: event.TimeStamp,
          },
        });
        break;
      case ReceiveMessageCommand.UpdateUserToken:
        this.#event$.next({
          action: 'updateToken',
          params: { token: event.Token },
        });
        break;
      case ReceiveMessageCommand.Group:
        {
          switch (event.Type) {
            case ReceiveGroupMessageCommand.Join:
              this.#event$.next({
                action: 'joinGroup',
                params: {
                  groupId: event.GroupID,
                  groupImg: event.GroupImg ?? '',
                  groupName: event.GroupName ?? '',
                },
              });
              break;
            case ReceiveGroupMessageCommand.Remove: // NOTE: group disbanded
            case ReceiveGroupMessageCommand.Leave: // NOTE: userId removed from group
              if (event.Type === ReceiveGroupMessageCommand.Leave) {
                this.#event$.next({
                  action: 'leaveGroup',
                  params: {
                    groupId: event.GroupID,
                    userId: event.UserID ?? 0,
                  },
                });
              } else {
                this.#event$.next({
                  action: 'disbandGroup',
                  params: {
                    groupId: event.GroupID,
                  },
                });
              }
              this.#auth &&
                this.#queue$.next({
                  Cmd: SendMessageCommand.GroupUnsubscribe,
                  UID: this.#auth.UserID,
                  GroupID: event.GroupID,
                });
              break;
            case ReceiveGroupMessageCommand.Purge:
              this.#event$.next({
                action: 'purgeMessages',
                params: {
                  type: 'group',
                  sessionId: event.GroupID,
                  userId: event.UserID,
                },
              });
              break;
          }
        }
        break;
      case ReceiveMessageCommand.Connected:
        this.#logger.info('Connected', this.#endpoint, this.#socketId);
        break;
      case ReceiveMessageCommand.Online:
        this.#event$.next({
          action: 'online',
          params: { userId: event.FriendID },
        });
        break;
      case ReceiveMessageCommand.Offline:
        this.#event$.next({
          action: 'offline',
          params: { userId: event.FriendID },
        });
        break;
      case ReceiveMessageCommand.HeartBeatResponse:
        this.#heartbeat$.next(Date.now());
        break;
      case ReceiveMessageCommand.DeleteChatMessage:
        this.#event$.next({
          action: 'deleteMessage',
          params: {
            type: 'session',
            sessionId: event.ChatSessionID,
            messageId: event.ChatID,
          },
        });
        break;
      case ReceiveMessageCommand.DeleteGroupChatMessage:
        this.#event$.next({
          action: 'deleteMessage',
          params: {
            type: 'group',
            sessionId: event.GroupID,
            messageId: event.GroupChatID,
          },
        });
        break;
      case ReceiveMessageCommand.DeleteUserMessage:
        this.#event$.next({
          action: 'purgeMessages',
          params: { userId: event.UserID },
        });
        break;
      case ReceiveMessageCommand.GroupNewApplication:
        this.#event$.next({
          action: 'groupApplication',
          params: {
            groupId: event.GroupID,
            applicationCount: event.GroupJoinApplyCount,
          },
        });
        break;
      case ReceiveMessageCommand.BanGroupUser:
        this.#event$.next({
          action: 'muteGroupUser',
          params: {
            groupId: event.GroupID,
            userId: event.UserID,
            mute: event.Status === 0,
          },
        });
        break;
      default:
        break;
    }
  }

  #createWebSocketConnection() {
    if (!this.#subscriptions?.closed) {
      this.#subscriptions?.unsubscribe();
      this.#subscriptions = new Subscription();
    }

    if (!this.#socket$?.closed) {
      this.#socket$?.complete();
      this.#socket$ = null;
    }

    const connectionId = (this.#socketId = nanoid(12));
    this.#logger.info('Connecting', `${connectionId} to ${this.#endpoint}`);
    this.#state$.next('connecting');

    this.#socket$ = webSocket<WebSocketMessage>({
      url: this.#endpoint,
      openObserver: {
        next: () => {
          this.#state$.next('open');
          this.#event$.next({
            action: 'stateChange',
            params: { state: 'open', endpoint: this.#endpoint },
          });
          this.#logger.info('Opened', this.#endpoint, connectionId);

          const device = getDeviceIdentifier();
          this.#auth &&
            this.#socket$?.next(
              pickBy(isNotNil, {
                Cmd: SendMessageCommand.Login,
                UID: this.#auth.UserID,
                Token: this.#auth.Token,
                Lang: this.#locale,
                Gateway: new URL(this.#endpoint).hostname,
                IsWeb: true,
                D: device,
                Brand: device !== Device.PWA ? this.#brand : undefined,
                WSID: connectionId,
              }),
            );
        },
      },
      closeObserver: {
        next: () => {
          this.#state$.next('closed');
          this.#event$.next({
            action: 'stateChange',
            params: { state: 'closed' },
          });
          this.#signedIn$.next(false);
          this.#logger.info('Closed', this.#endpoint, connectionId);
        },
      },
      closingObserver: {
        next: () => {
          this.#state$.next('ended');
          this.#event$.next({
            action: 'stateChange',
            params: { state: 'ended' },
          });
          this.#signedIn$.next(false);
        },
      },
    });

    const visibility$ = combineLatest([
      merge(fromEvent(window, 'online'), fromEvent(window, 'offline')).pipe(
        startWith(navigator.onLine),
        map(() => navigator.onLine),
      ),
      fromEvent(document, 'visibilitychange').pipe(
        startWith(!document.hidden),
        map(() => !document.hidden),
      ),
    ]);

    const visibility$$ = visibility$.pipe(distinctUntilChanged(), skipUntil(this.#signedIn$)).subscribe({
      next: ([online, visible]) => {
        if (online && visible && this.state === 'closed') {
          this.#createWebSocketConnection();
        } else if (!online || !visible) {
          this.#socket$?.complete();
        }
      },
    });

    const queue$$ = combineLatest([this.#queue$, this.#signedIn$, this.#checkConnection$])
      .pipe(filter(([event, isSignedIn, checkConnection]) => event !== null && isSignedIn && checkConnection === null))
      .subscribe(([event]) => {
        this.#socket$?.next({ ...event, D: getDeviceIdentifier() });
      });

    const socket$$ = this.#socket$
      .pipe(
        retry({
          count: 2,
          delay: (err, count) => {
            this.#logger.error('Retry', this.#endpoint, count, err);
            return timer(count * 5 * 1000).pipe(
              skipUntil(visibility$.pipe(filter(([online, visible]) => online && visible))),
            );
          },
        }),
        catchError((error) => {
          this.#logger.error(error);
          this.#dynamicEndpointManager.revokeSocketEndpoint(this.#endpoint);

          return EMPTY;
        }),
        tap((event) => {
          this.#logger.info('Message received', event.Cmd, event);
        }),
      )
      .subscribe({
        next: (event) => {
          this.#state$.next('connected');
          return this.#handleIncomingEvent(event);
        },
        error: (error) => {
          this.#state$.next('closed');
          this.#logger.error(error);
        },
      });

    const heartbeat$$ = combineLatest([this.#heartbeat$, this.#signedIn$, interval(10000)])
      .pipe(filter(([lastHeartbeatTime, signedIn]) => lastHeartbeatTime !== 0 && signedIn))
      .subscribe(([lastHeartbeatTime]) => {
        const delta = Date.now() - lastHeartbeatTime;
        if (delta >= 10_000 && this.#auth) {
          this.#queue$.next({
            Cmd: SendMessageCommand.HeartBeat,
            UID: this.#auth.UserID,
          });
        } else if (delta > 30_000) {
          this.#logger.error('Heartbeat', 'timeout');
          this.#state$.next('ended');
          this.#event$.next({
            action: 'stateChange',
            params: { state: 'ended' },
          });
          this.#createWebSocketConnection();
        }
      });

    const checkConnection$$ = interval(1000)
      .pipe(
        withLatestFrom(this.#checkConnection$, this.#heartbeat$, this.#signedIn$),
        filter(
          ([, checkConnectionTime, lastHeartbeatTime, signedIn]) =>
            checkConnectionTime !== null && lastHeartbeatTime !== 0 && signedIn,
        ),
      )
      .subscribe(([, checkConnectionTime, lastHeartbeatTime]) => {
        if (lastHeartbeatTime - (checkConnectionTime || 0) < 1000) {
          this.#checkConnection$.next(null);
        } else {
          this.#createWebSocketConnection();
        }
      });

    this.#subscriptions.add(socket$$);
    this.#subscriptions.add(heartbeat$$);
    this.#subscriptions.add(queue$$);
    this.#subscriptions.add(checkConnection$$);
    this.#subscriptions.add(visibility$$);
  }
}
